import { Injectable } from '@angular/core';

import { LOAD_SANNA_LICENCES_CONSTANTS, LOAD_SANNA_LICENCES_CONSTANTS_SUCCESS } from '@core/store/actions';
import { AppState } from '@core/store/app.reducers';
import { Store } from '@ngrx/store';
import { ofType, Actions } from '@ngrx/effects';

import {
  IComuna,
  IConstantsResponseItems,
  IConstantsResponse,
  IObj,
  IProvincia,
  IRegionProvincias,
  IProfileByPath,
} from '../data/models/constants.interface';
import { ICrearLicenciaContingencia } from '../data/models/sanna-licencia-api.interface';
import { GeneralUtils } from '@shared/utils/general.utils';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class ConstantsService {
  /** Mensajes estáticos que son usados de manera transversal en varias vistas/controladores */
  mensaje = {
    error: {
      inesperado: 'Error inesperado',
      obteniendoLicencia: 'Error obteniendo licencia',
      obteniendoConstantes:
        'No se ha podido obtener datos importantes para el funcionamiento. Favor, intente recargar la página.',
      formularioSinEnviar: 'No se ha enviado el formulario',
      formularioSinGuardar: 'No se ha guardado el formulario',
      archivoNoSubido: 'Error subiendo archivo. Intentelo nuevamente y verifique formato',
      archivoExedeLimiteTamano: '', // se setea en el constructor
      maxFileSizeMessage: '', // se setea en el constructor
      archivoNoEncontrado: 'No se pudo obtener el archivo',
      loginDatosIncorrectos: 'Error en datos de ingreso. Favor intente nuevamente',
      formatoNoCompatible: 'Formato del archivo no compatible. Debe ser PDF, JPG o PNG. Intentelo nuevamente.',
    },
    tooltip: {
      formatoRutCompleto: 'Formato 12345678-9 (incluir guión y sin puntos, K mayúscula)',
      formatoRutIncompleto: 'Formato: 123456789 (sin puntos, ni guión, ni DV)',
    },
    alerta: {
      requiereAtencion: 'Existen acciones importantes que requieren de su atención',
      camposInvalidos: 'Hay elementos obligatorios que deben ser completados',
      archivoSubidoExitosamente: 'Archivo subido exitosamente',
      noHayArchivosPreviamentesAdjuntos: 'No hay archivos previamente adjuntos',
      noHayRegistros: 'No se han encontrado registros',
      datosActualizadosExitosamente: 'Datos actualizados exitosamente',
      datosDescartados: 'Se ha descartado el formulario y se han eliminado los datos ingresados',
      datosEnviadosExitosamente: 'Datos enviados de manera exitosa',
    },
    desarrollo: {
      pendienteSprint: 'Pendiente para <b>Sprint #7</b>',
    },
  };

  tiempos = {
    errorGrave: 8000,
  };

  /**
   * Esta variable contiene todos los valores estáticos de listados que se requieren y se traen de la BD
   * Una versión estática que sirve para ver su estructura se puede encontrar en assets/parametrosTransversales.json
   * En https://jsoneditoronline.org/ se puede transformar para agregar las doble comillas
   */
  private data: IConstantsResponseItems;

  /** Objeto que almacena estructura variable de acuerdo a contingencia que sea seleccionada a la hora de crear licencia médica */
  informeComplementario: ICrearLicenciaContingencia = {
    cancer: {
      nombre: 'CÁNCER',
      identificador: 'A',
      selected: false,
      etapa: [
        {
          id: 0,
          desc: 'SOSPECHA',
          selected: false,
        },
        {
          id: 1,
          desc: 'CONFIRMACIÓN DIAGNÓSTICO',
          selected: false,
        },
        {
          id: 2,
          desc: 'TRATAMIENTO',
          selected: false,
        },
        {
          id: 3,
          desc: 'SEGUIMIENTO',
          selected: false,
        },
        {
          id: 4,
          desc: 'RECIDIVA',
          selected: false,
        },
      ],
      fechaInforme: '',
      descripcion: '',
    },
    transplante: {
      nombre: 'TRASPLANTE',
      identificador: 'B',
      selected: false,
      child: [
        {
          id: 0,
          desc: 'ÓRGANO SÓLIDO',
          selected: false,
          child: [
            {
              id: 0,
              desc: 'TRASPLANTE EFECTUADO, CON FECHA',
              selected: false,
              fecha: '',
            },
            {
              id: 1,
              desc: 'NO SE HA EFECTUADO EL TRANSPLANTE',
              selected: false,
              child: [
                {
                  id: 0,
                  desc: 'Menor inscrito en Registro de Nacional de potenciales receptores de órganos',
                  more: 'Debe acreditar con certificado de Coordinadora Nacional de Trasplante',
                  selected: false,
                },
                {
                  id: 1,
                  desc: 'En estudio donante vivo',
                  more: '',
                  selected: false,
                },
              ],
            },
          ],
        },
        {
          id: 1,
          desc: 'PROGENITORES HEMATOPOYÉTICOS',
          selected: false,
          fecha: '',
        },
      ],
    },
    estadoTerminal: {
      nombre: 'ESTADO TERMINAL',
      identificador: 'C',
      selected: false,
      child: [
        {
          id: 0,
          desc: 'FASE O ESTADO TERMINAL DE LA VIDA',
          selected: false,
        },
        {
          id: 1,
          desc: 'TRATAMIENTO DESTINADO AL ALIVIO DEL DOLOR Y CUIDADOS PALIATIVOS POR CÁNCER AVANZADO',
          selected: false,
          more: 'Indicar patología, la que debe estar prevista dentro de las Garantías Explícitas en Salud establecidas en la Ley N° 19.966 y sus reglamentos, salvo mayores de 15 años y menores de 18 años de edad',
          patologia: '',
        },
      ],
    },
    accidenteGrave: {
      nombre: 'ACCIDENTE GRAVE',
      identificador: 'D',
      selected: false,
      fechaDelAccidente: '',
      child: [
        {
          title: 'BREVE RELACIÓN DE LAS CIRCUNSTANCIAS EN QUE SE PRODUJO EL ACCIDENTE',
          desc: '',
        },
        {
          title: 'BREVE HISTORIA CON FUNDAMENTOS CLÍNICOS Y/O DE LABORATORIO Y/O DE IMAGENOLOGÍA',
          desc: '',
        },
        {
          title:
            'EVOLUCIÓN, TRATAMIENTOS MÉDICOS Y/O QUIRÚRGICOS EFECTUADOS (FECHA DE OPERACIÓN, SI PROCEDE), RESULTADOS OBTENIDOS',
          desc: '',
        },
        {
          title: 'CONCLUSIÓN U OTRO COMENTARIO',
          desc: '',
        },
      ],
    },
    enfermedadGrave: {
      nombre: 'ENFERMEDAD GRAVEENFERMEDAD GRAVE QUE REQUIERA HOSPITALIZACIÓN EN UNA UNIDAD DE CUIDADOS INTENSIVOS O DE TRATAMIENTOS INTERMEDIOS',
      identificador: 'E',
      selected: false,
      fecha: '',
      child: [
        {
          title: 'BREVE RELACIÓN DE LAS CIRCUNSTANCIAS',
          desc: '',
        },
        {
          title: 'BREVE HISTORIA CON FUNDAMENTOS CLÍNICOS Y/O DE LABORATORIO Y/O DE IMAGENOLOGÍA',
          desc: '',
        },
        {
          title:
            'EVOLUCIÓN, TRATAMIENTOS MÉDICOS Y/O QUIRÚRGICOS EFECTUADOS (FECHA DE OPERACIÓN, SI PROCEDE), RESULTADOS OBTENIDOS',
          desc: '',
        },
        {
          title: 'CONCLUSIÓN U OTRO COMENTARIO',
          desc: '',
        },
      ],
    },
  };

  /** Imagen vectorial: linea gris de fondo */
  svgs = {
    fondoCuadrado: `<svg width="100%" height="100%" viewBox="0 0 3300 2550" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:1.5;">
    <g transform="matrix(1,0,0,1,1.87384,-349.859)">
        <path d="M3195.44,988.517C3195.44,954.534 3167.85,926.945 3133.87,926.945L162.385,926.945C128.403,926.945 100.814,954.534 100.814,988.517L100.814,2261.2C100.814,2295.18 128.403,2322.77 162.385,2322.77L3133.87,2322.77C3167.85,2322.77 3195.44,2295.18 3195.44,2261.2L3195.44,988.517Z" style="fill:none;stroke:rgb(201,201,201);stroke-width:4.17px;"/>
    </g>
</svg>`,
  };

  sA: IObj = {
    id: '0', // No se define vació porque como campo de formulario los sekectores requieren como minimo 1 char
    description: 's/a',
  };

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private generalUtils: GeneralUtils,
    private platform: Platform,
  ) {
    this.platform.ready().then(() => {
      this.store.dispatch(LOAD_SANNA_LICENCES_CONSTANTS());
      this.getConstants();
    });
  }

  getConstants() {
    this.actions$.pipe(ofType(LOAD_SANNA_LICENCES_CONSTANTS_SUCCESS)).subscribe(({ constants }) => {
      this.data = constants[0];
      // Setea mensaje de cantidad máxima
      this.mensaje.error.archivoExedeLimiteTamano =
        'El archivo seleccionado sobrepasa el límite establecido (' +
        this.generalUtils.bytesToSize(this.getConstanteLimiteUploadFilesBytes()) +
        '). Favor, intente subir otro.';
    });
  }

  getConstanteLimiteUploadFilesBytes(): number {
    return this.data?.limiteUploadFilesBytes;
  }
  getConstanteSexo(): IObj[] {
    return [
      {
        id: '-1',
        extra: '-1',
        description: this.sA.description,
      },
      ...(this.data?.sexo ? this.data.sexo : [])
    ];
  }
  getConstanteSiNo(): IObj[] {
    return [this.sA, ...(this.data?.siNo ? this.data.siNo : [])];
  }
  getConstanteNumeroDeDias(): IObj[] {
    return [this.sA, ...(this.data?.numeroDeDias ? this.data?.numeroDeDias : [])];
  }
  getConstanteNumeroDeDiasTraspaso(): IObj[] {
    return [this.sA, ...(this.data?.numeroDeDiasTraspaso ? this.data?.numeroDeDiasTraspaso : [])];
  }
  getConstanteTipoReposo(): IObj[] {
    return [ this.sA,...(this.data?.tipoReposo ? this.data.tipoReposo : [])];
  }
  getConstanteJornada(): IObj[] {
    return [this.sA, ...(this.data?.jornada ? this.data?.jornada : [])];
  }
  getConstanteLugarReposo(): IObj[] {
    return [this.sA, ...(this.data?.lugarReposo ? this.data?.lugarReposo : [])];
  }
  getConstanteTiposDeTratamiento(): IObj[] {
    return [this.sA, ...(this.data?.tiposDeTratamiento ? this.data?.tiposDeTratamiento : [])];
  }
  getConstanteTiposMedioDePago(): IObj[] {
    return this.data?.tiposMedioDePago;
  }
  getConstanteBancos(): IObj[] {
    return this.data?.bancos;
  }
  getConstanteTiposDeCuenta(): IObj[] {
    return this.data?.tiposDeCuenta;
  }
  getConstanteEntidadesPagadoras(): IObj[] {
    return this.data?.entidadesPagadoras;
  }
  getConstanteCodigosTipoDeArchivo(): IObj[] {
    return this.data?.codigosTipoDeArchivo;
  }
  getConstantecodigosEntidadCotizacionSanna(): IObj[] {
    return this.data?.codigosEntidadCotizacionSanna;
  }
  getConstanteActividadesLaborales(): IObj[] {
    // usa s/a con valor -1 por que 0 es un valor seleccionable
    return [
      { id: '-1', description: this.sA.description },
      ...(this.data?.actividadesLaborales ? this.data?.actividadesLaborales : [])
    ];
  }
  getConstanteOcupaciones(): IObj[] {
    return [this.sA, ...(this.data?.ocupaciones ? this.data?.ocupaciones : [])];
  }
  getConstanteRegimenesProvisionales(): IObj[] {
    return [this.sA, ...(this.data?.regimenesProvisionales ? this.data?.regimenesProvisionales : [])];
  }
  getConstanteCalidadesTrabajador(): IObj[] {
    return [this.sA, ...(this.data?.calidadesTrabajador ? this.data?.calidadesTrabajador : [])];
  }
  getConstanteTiposDeArchivos(): IObj[] {
    return this.data?.tiposDeArchivos;
  }
  getConstanteTiposDeArchivosSolicitud(): IObj[] {
    return this.data?.tiposDeArchivosSolicitables;
  }
  getConstanteTiposDeArchivosAdicional(): IObj[] {
    return this.data?.tiposDeArchivosAdicional;
  }
  getConstanteTiposDeArchivosAccionesComplementarias(): IObj[] {
    return this.data?.tiposDeArchivosAccionesComplementarias;
  }
  getConstanteRegimenLey16744(): IObj[] {
    return [this.sA, ...(this.data?.regimenLey16744 ? this.data?.regimenLey16744 : [])];
  }
  getConstanteRegimenDeSalud(): IObj[] {
    return [this.sA, ...(this.data?.regimenDeSalud ? this.data?.regimenDeSalud : [])];
  }
  getConstanteUltimoTipoDeContrato(): IObj[] {
    return this.data?.ultimoTipoDeContrato;
  }
  getConstanteTipoDeFeriadoLegal(): IObj[] {
    return [this.sA, ...(this.data?.tipoDeFeriadoLegal ? this.data?.tipoDeFeriadoLegal : [])];
  }
  getConstanteIsapres(): IObj[] {
    return [this.sA, ...(this.data?.isapres ? this.data?.isapres : [])];
  }
  getConstanteRegimenPrevisionCodigoDescripcion(): IObj[] {
    return [this.sA, ...(this.data?.regimenPrevisionCodigoDescripcion ? this.data?.regimenPrevisionCodigoDescripcion : [])];
  }
  getConstanteRegimenPrevisionLetra(): IObj[] {
    return [this.sA, ...(this.data?.regimenPrevisionLetra ? this.data?.regimenPrevisionLetra : [])];
  }
  getConstanteInstitucionesPrevisionales(): IObj[] {
    return [
      {
        id: '',
        description: this.sA.description,
      },
      ...(this.data?.institucionesPrevisionales ? this.data?.institucionesPrevisionales : []),
    ];
  }
  getConstanteMeses(): IObj[] {
    return this.data?.meses;
  }
  getConstanteEntidadPagadoraSubsidio(): IObj[] {
    return this.data?.entidadPagadoraSubsidio;
  }
  getConstanteEstadoRevisionDocumento(): IObj[] {
    return this.data?.estadoRevisionDocumento;
  }
  getEstadoLicenciaSanna(): IObj[] {
    return this.data?.estadoLicenciaSanna ? this.data?.estadoLicenciaSanna : [];
  }
  getEstadoLicenciaSannaEntidad(): IObj[] {
    return this.data?.estadoLicenciaSannaEntidad;
  }
  getTiposDeDictamenes(): IObj[] {
    return this.data?.tiposDeDictamenes;
  }
  getTiposContinuacion(): IObj[] {
    return this.data?.tiposContinuacion;
  }
  getCausalesRechazo(): IObj[] {
    return this.data?.causalesDeRechazo ?? [];
  }
  getCausalesDeModificacion(): IObj[] {
    return this.data?.causalesDeModificacion ?? [];
  }
  getCausalesAutorizacion(): IObj[] {
    return this.data?.causalesAutorizacion ?? [];
  }
  getCausalesDeReduccion(): IObj[] {
    return this.data?.causalesDeReduccion ?? [];
  }
  getCausalesPendienteDeResolucion(): IObj[] {
    return this.data?.causalesDelPendienteDeResolucion ?? [];
  }
  getOtrasCausalesUObservaciones(): IObj[] {
    return this.data?.otrasCausalesUObservaciones ?? [];
  }
  getConstanteMotivosDeSolicitudDeAntecedentes(): IObj[] {
    return this.data?.motivosDeSolicitudDeAntecedentes;
  }
  getConstanteRecepcionDeAntecedentes(): IObj[] {
    return this.data?.recepcionDeAntecedentes;
  }
  getConstanteTiposResolucion(): IObj[] {
    return this.data?.tiposResolucion;
  }
  getConstanteTiposDerechoSubsidio(): IObj[] {
    return this.data?.tiposDerechoSubsidio;
  }
  getConstanteNacionalidades(): IObj[] {
    return this.data?.nacionalidades;
  }
  getConstanteTiposApelacion(): IObj[] {
    return this.data?.tiposApelacion;
  }
  getConstanteEstadoSolicitudDocumento(): IObj[] {
    return this.data?.estadosSolicitudDocumento;
  }
  getConstanteEstadoNotificacionSuseso(): IObj[] {
    return this.data?.estadosNotificacionSucesos;
  }
  getConstanteTipoProfesional(): IObj[] {
    return this.data?.tiposProfesional;
  }

  /**
   * Obtiene todas las Regiones y Comunas
   *
   * @returns Regiones y Comunas
   */
  getConstanteRegionesComunas(): IRegionProvincias[] {
    return this.data?.regionesComunas;
  }

  /**
   * Busca de acuerdo al id de un objeto
   *
   * @param obj - Listado con Objetos del tipo [{id, description}...]
   * @param id - Identificador a ser buscado
   * @return Descripción del Objeto
   */
  getObjDescription(obj: IObj[], id: string): string {
    const nodo = obj.find((elem) => elem.id === id);
    return nodo === undefined ? '' : nodo.description;
  }

  /**
   * Busca de acuerdo a la description de un objeto
   *
   * @param obj - Listado con Objetos del tipo [{id, description}...]
   * @param description - Identificador a ser buscado
   * @return id del Objeto
   */
  getObjId(obj: IObj[], description: string): string {
    const nodo = obj.find((elem) => elem.description === description);
    return nodo === undefined ? '' : nodo.id;
  }

  /**
   * Busca de acuerdo al id de un objeto
   *
   * @param obj - Listado con Objetos del tipo [{id, description}...]
   * @param id - Identificador a ser buscado
   * @return Objeto completo
   */
  getObj(obj: IObj[], id: string): IObj {
    const nodo = obj.find((elem) => elem.id === id);
    return nodo === undefined ? null : nodo;
  }

  /**
   * Obtiene el nombre de la región
   *
   * @param regionId - Identificador de la región
   * @returns Nombre de la región
   */
  getRegionName(regionId: number): string {
    const regionNombre = this.getConstanteRegionesComunas().find((region) => region.id === regionId);
    return regionNombre === undefined ? '' : regionNombre.name;
  }

  /**
   * Obtiene el nombre de la comuna
   *
   * @param comunaId- Identificador de la omuna
   * @returns Nombre de la comuba
   */
  getComunaName(comunaId: number): string {
    let comunaNombre = '';
    this.getConstanteRegionesComunas().forEach((region) => {
      region.provincias.forEach((provincia) => {
        provincia.comunas.forEach((comuna) => {
          if (comuna.id === comunaId) {
            comunaNombre = comuna.name;
          }
        });
      });
    });
    return comunaNombre === undefined ? '' : comunaNombre;
  }

  /**
   * Obtiene nombre e identificador de Provincia
   *
   * @param comunaId - Identificador de Comuna
   * @returns Provincia (nombre e identificador)
   */
  getProvincia(comunaId: number): IProvincia {
    let provinciaEncontrada: IProvincia;
    this.getConstanteRegionesComunas().forEach((region) => {
      region.provincias.forEach((provincia) => {
        provincia.comunas.forEach((comuna) => {
          if (comuna.id === comunaId) {
            provinciaEncontrada = provincia;
          }
        });
      });
    });
    return provinciaEncontrada;
  }

  /**
   * Obtiene Comunas de acuerdo a identificador de Región
   *
   * @param regionId - Identificador de Región
   * @returns Listado de Comunas
   */
  getComunas(regionId: number): IComuna[] {
    const comunas: IComuna[] = [];
    this.getConstanteRegionesComunas().forEach((region) => {
      if (region.id === regionId) {
        region.provincias.forEach((provincia) => {
          provincia.comunas.forEach((comuna) => {
            comunas.sort((a, b) => a.name.localeCompare(b.name)); // ordena
            comunas.push(comuna);
          });
        });
      }
    });
    return comunas;
  }

  /**
   * @param sex identificador con valor `F` o `M`
   */
  getSexo(sex: string): IObj {
    const sexes = this.getConstanteSexo();
    return sexes.find((opt) => opt.extra == sex);
  }

  /**
   * @param sex identificador con valor 0 o 1
   */
  getSexoById(sex: number): IObj {
    const sexes = this.getConstanteSexo();
    return sexes.find((opt) => parseInt(opt.id) == sex);
  }

  getProfileByPath(): IProfileByPath[] {
    return this.data?.profileByPath;
  }

  getProfesionalTypeByDescription(description: string): IObj {
    const desc = description.toString().toLowerCase();
    const profesionalTypes = this.getConstanteTipoProfesional();
    return profesionalTypes.find((opt) => opt.description === desc);
  }
}
